/**
 * Radar & aviation bearing lines (issue #171): dashed, pulsing lines fan from
 * the turbine-array centroid to off-map MoD radar sites and Newcastle Airport.
 * Pure, three.js-free helpers — unit-tested in Node. The three.js wrapper is
 * BearingLinesLayer (bearings.ts).
 *
 * Scene furniture only (hard constraint #1): illustrative orientation markers
 * with no bearing on any LOS result. Coordinates come exclusively from
 * data/config/facilities.json (hard constraint #7); a missing file disables
 * the layer, and a bad per-facility row skips only that line.
 *
 * Lines are fixed-altitude sky furniture (~380 m AOD) — never terrain-sampled
 * (hard constraint #6 does not apply here; altitude is a deliberate constant,
 * not invented ground). Each line is truncated where its true planar bearing
 * first crosses the core AOI rectangle; labels show the full crow-flies distance
 * to the real facility.
 */
import {
  bngToLocal,
  bearingDegFromForward,
  forwardFromBearingDeg,
  planarDistance,
  type LocalXZ,
} from '../core/coordinates';
import type { Facility } from '../core/manifest';

/** Turbine-array centroid (published Ray II layout reference). */
export const TURBINE_CENTROID_EASTING = 393676;
export const TURBINE_CENTROID_NORTHING = 588914;

/** Fixed altitude for every bearing segment, metres AOD — sky furniture. */
export const BEARING_LINES_ALTITUDE_M = 380;

/** Core AOI rectangle [minE, minN, maxE, maxN] — matches raypath/aoi.py CORE_BOUNDS. */
export const CORE_AOI_BOUNDS: [number, number, number, number] = [388500, 582200, 400500, 594200];

const COLOR_MOD_RADAR = 0xe63946;
const COLOR_CIVIL_AVIATION = 0xffb020;

export interface BuiltBearingLine {
  id: string;
  start: LocalXZ;
  end: LocalXZ;
  bearingDeg: number;
  distanceM: number;
  labelText: string;
  color: number;
}

export function turbineCentroidLocal(): LocalXZ {
  return bngToLocal(TURBINE_CENTROID_EASTING, TURBINE_CENTROID_NORTHING);
}

export function facilityHasValidCoords(facility: Pick<Facility, 'easting' | 'northing'>): boolean {
  return (
    typeof facility.easting === 'number' &&
    Number.isFinite(facility.easting) &&
    typeof facility.northing === 'number' &&
    Number.isFinite(facility.northing)
  );
}

export function colorForFacilityKind(kind: Facility['kind']): number {
  return kind === 'civil_aviation' ? COLOR_CIVIL_AVIATION : COLOR_MOD_RADAR;
}

/** Full crow-flies planar distance (metres) between two local x/z points. */
export function crowFliesDistanceM(a: LocalXZ, b: LocalXZ): number {
  return planarDistance(a, b);
}

/** Distance label fragment with one decimal kilometre, e.g. "37.3". */
export function formatBearingDistanceKm(distanceM: number): string {
  return (distanceM / 1000).toFixed(1);
}

/** Billboard label text: "Name · NN.N km". */
export function bearingLabelText(name: string, distanceM: number): string {
  return `${name} · ${formatBearingDistanceKm(distanceM)} km`;
}

/**
 * Where a ray from `originE`/`originN` along `bearingDeg` first exits the axis-
 * aligned AOI rectangle. Returns null when the ray never crosses the boundary in
 * the outward (positive-t) direction — e.g. origin outside the box shooting away.
 */
export function rayExitAoiBoundaryBng(
  originE: number,
  originN: number,
  bearingDeg: number,
  bounds: [number, number, number, number] = CORE_AOI_BOUNDS,
): { easting: number; northing: number } | null {
  const fwd = forwardFromBearingDeg(bearingDeg);
  const de = fwd.x;
  const dn = -fwd.z;
  if (Math.abs(de) < 1e-12 && Math.abs(dn) < 1e-12) return null;

  const [minE, minN, maxE, maxN] = bounds;
  let tMin = Infinity;

  if (de > 1e-9) {
    const t = (maxE - originE) / de;
    if (t > 0) tMin = Math.min(tMin, t);
  } else if (de < -1e-9) {
    const t = (minE - originE) / de;
    if (t > 0) tMin = Math.min(tMin, t);
  }

  if (dn > 1e-9) {
    const t = (maxN - originN) / dn;
    if (t > 0) tMin = Math.min(tMin, t);
  } else if (dn < -1e-9) {
    const t = (minN - originN) / dn;
    if (t > 0) tMin = Math.min(tMin, t);
  }

  if (!Number.isFinite(tMin) || tMin === Infinity) return null;

  return {
    easting: originE + tMin * de,
    northing: originN + tMin * dn,
  };
}

export interface BuildBearingLineOrigin {
  easting: number;
  northing: number;
}

/** Builds one truncated bearing segment, or null when coords/boundary are invalid. */
export function buildBearingLine(
  facility: Facility,
  origin: BuildBearingLineOrigin = {
    easting: TURBINE_CENTROID_EASTING,
    northing: TURBINE_CENTROID_NORTHING,
  },
  bounds: [number, number, number, number] = CORE_AOI_BOUNDS,
): BuiltBearingLine | null {
  if (!facilityHasValidCoords(facility)) return null;

  const start = bngToLocal(origin.easting, origin.northing);
  const facilityLocal = bngToLocal(facility.easting, facility.northing);
  const dx = facilityLocal.x - start.x;
  const dz = facilityLocal.z - start.z;
  const bearingDeg = bearingDegFromForward(dx, dz);
  const distanceM = crowFliesDistanceM(start, facilityLocal);

  const exit = rayExitAoiBoundaryBng(origin.easting, origin.northing, bearingDeg, bounds);
  if (!exit) return null;

  const end = bngToLocal(exit.easting, exit.northing);

  return {
    id: facility.id,
    start,
    end,
    bearingDeg,
    distanceM,
    labelText: bearingLabelText(facility.name, distanceM),
    color: colorForFacilityKind(facility.kind),
  };
}

/** Builds every valid bearing line, skipping bad rows silently (callers log). */
export function buildBearingLines(
  facilities: Facility[],
  origin?: BuildBearingLineOrigin,
  bounds?: [number, number, number, number],
): BuiltBearingLine[] {
  const lines: BuiltBearingLine[] = [];
  for (const facility of facilities) {
    const line = buildBearingLine(facility, origin, bounds);
    if (line) lines.push(line);
  }
  return lines;
}
